import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'admin/email/defaultConfiguration';

export interface IDefaultEmailConfigurationRequest {
	provider?: Api.EmailProviderType;
	domain?: string;
	accountId: string;
	enabled?: boolean;
}

/**
 * Fetches the resolved configuration (effective send limits + seconds-between-sends) for an
 * account's send domain, used to show the in-effect values as placeholder hints on a configuration
 * row.
 * `GET admin/email/defaultConfiguration?provider=&domain=&accountId=`.
 *
 * The endpoint 500s for a domain the account has no configuration for, so callers gate `enabled` on
 * a plausible domain; failures leave the hints blank rather than surfacing an error.
 */
export function useDefaultEmailConfigurationQuery({
	provider,
	domain,
	accountId,
	enabled = true,
}: IDefaultEmailConfigurationRequest) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: [BASE_QUERY_KEY, accountId, provider, domain],
		refetchOnWindowFocus: false,
		retry: false,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.IEmailProviderConfiguration>(
				`${BASE_QUERY_KEY}?provider=${encodeURIComponent(provider ?? '')}&domain=${encodeURIComponent(domain ?? '')}&accountId=${encodeURIComponent(accountId)}`,
				'GET'
			),
		enabled: enabled && !!provider && !!domain && !!accountId,
	});
}
